import React from 'react'
import { useLanguage } from './LanguageProvider'
import { mergeTranslations } from './utils/mergeTranslations'
import type { Translations } from './types'

/**
 * Type-safe translation hook
 * Returns nested translation object for autocomplete and type-checking
 *
 * @example
 * const T = useTranslations()
 * return <button>{T.common.login}</button>  // ✅ Autocomplete works!
 */
export function useTranslations(): Translations {
  const { language, messages } = useLanguage()

  return React.useMemo(() => {
    // Get English messages (always loaded as fallback)
    const en = messages.en

    // If current language is English, return as-is
    if (language === 'en') {
      return en as Translations
    }

    // Otherwise, merge current language with English fallback
    const currentLocale = messages[language]
    if (!currentLocale) {
      // Locale not loaded yet - return English
      return en as Translations
    }

    // Deep merge: overlay current language on top of English
    return mergeTranslations(en as Translations, currentLocale as Partial<Translations>)
  }, [language, messages])
}
